import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GET_COMPANIES_V2 } from 'features/Companies/queries-legacy'
import { canRead, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { CompaniesList } from './List'
import { GTCompaniesV2Response } from 'gql/types.generated'

const FIRST_PAGE = 1
const COMPANIES_PER_PAGE = 1000

const messages = defineMessages({
  helmet: {
    id: 'companies.helmet',
    defaultMessage: 'Companies | Nexbus Admin'
  }
})

const CompaniesView = (): TSFixMe => {
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const { loading, error, data, refetch, networkStatus } = useQuery<{ companiesV2: GTCompaniesV2Response }>(
    GET_COMPANIES_V2,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables: {
        limit: COMPANIES_PER_PAGE,
        page: FIRST_PAGE
      }
    }
  )

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (!canRead(FEATURES.COMPANY, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.COMPANY} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="companies.error.description"
            defaultMessage="There was an error while loading your companies"
          />
        </EmptyState.Description>
        <EmptyState.Description>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="companies.error.action" defaultMessage="Reload your companies" />
          </Button>
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.companiesV2?.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="companies.empty.description" defaultMessage="You don't have any companies yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/companies/new" kind="primary">
            <FormattedMessage id="companies.empty.action" defaultMessage="Add your first company" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = <CompaniesList companies={data?.companiesV2.items ?? []} />
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="companies.header.label" defaultMessage="Companies" />
        </Heading.H3>
        {canWrite(FEATURES.COMPANY, login.accessToken.policies) && (
          <Button as={NavLink} to="/companies/new" kind="primary">
            <FormattedMessage id="companies.header.new" defaultMessage="+ Create company" />
          </Button>
        )}
      </ContentHeader>
      <section>
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(CompaniesView)
